import { CHARACTER_KINDS, type CharacterDto, type CharacterKind, LANGUAGES, type MediaDto } from '@studio/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ImageSlots } from '@/components/ImageSlots';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/controls';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/form';
import { api, errorMessage } from '@/lib/api';
import { useBackgrounds } from '@/lib/queries';
const KIND_LABEL: Record<CharacterKind, string> = { organ: 'Cartoon organ', person: 'Person', mascot: 'Mascot', product: 'Product character' };
interface Draft {
  name: string;
  kind: CharacterKind;
  callName: string;
  visualDescription: string;
  voiceDescription: string;
  language: string;
  images: MediaDto[];
  notes: string;
  /** The clothes picture (0 or 1). */
  outfit: MediaDto[];
  backgroundId: string | null;
}

const empty: Draft = {
  name: '',
  kind: 'person',
  callName: '',
  visualDescription: '',
  voiceDescription: '',
  language: 'Hindi',
  images: [],
  notes: '',
  outfit: [],
  backgroundId: null,
};

export function CharacterDialog({
  character,
  folderId,
  open,
  onOpenChange,
}: {
  character: CharacterDto | null;
  folderId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const { data: backgrounds = [] } = useBackgrounds();
  const [d, setD] = useState<Draft>(empty);
  useEffect(() => {
    if (open)
      setD(
        character
          ? { ...character, outfit: character.outfitImage ? [character.outfitImage] : [], backgroundId: character.backgroundId ?? null }
          : empty,
      );
  }, [open, character]);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: d.name.trim(),
        folderId,
        images: d.images,
        outfitImage: d.outfit[0] ?? null,
        kind: d.kind,
        callName: d.callName,
        visualDescription: d.visualDescription,
        voiceDescription: d.voiceDescription,
        language: d.language,
        notes: d.notes,
        imageIds: d.images.map((i) => i.id),
        outfitImageId: d.outfit[0]?.id ?? null,
        backgroundId: d.backgroundId,
      };
      return character
        ? api(`/api/characters/${character.id}`, { method: 'PATCH', body })
        : api('/api/characters', { method: 'POST', body });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['characters'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      toast.success('Character saved');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{character ? `Edit ${character.name}` : 'New character'}</DialogTitle>
          <DialogDescription>
            Descriptions are in English: they go into the prompts. The same words every time keep the character looking
            the same.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            {(p) => (
              <Input {...p} value={d.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Stomach" />
            )}
          </Field>
          <Field label="Kind">
            {(p) => (
              <NativeSelect {...p} value={d.kind} onChange={(e) => set('kind', e.target.value as CharacterKind)}>
                {CHARACTER_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Called in prompts" hint='How a sentence names it, e.g. "the stomach". Blank: the name.'>
            {(p) => <Input {...p} value={d.callName} onChange={(e) => set('callName', e.target.value)} />}
          </Field>
          <Field label="Default language">
            {(p) => (
              <NativeSelect {...p} value={d.language} onChange={(e) => set('language', e.target.value)}>
                {LANGUAGES.map((l) => (
                  <option key={l}>{l}</option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field
            label="What it looks like"
            className="sm:col-span-2"
            hint="e.g. a chubby, glossy salmon-pink cartoon stomach with big round eyes and thin pink arms and legs"
          >
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                value={d.visualDescription}
                onChange={(e) => set('visualDescription', e.target.value)}
              />
            )}
          </Field>
          <Field label="Voice" className="sm:col-span-2" hint="e.g. a bright, cheerful young male voice">
            {(p) => (
              <Input {...p} value={d.voiceDescription} onChange={(e) => set('voiceDescription', e.target.value)} />
            )}
          </Field>
          <div className="sm:col-span-2">
            <div className="mb-1.5 text-sm font-medium">Reference pictures (1–3)</div>
            <ImageSlots images={d.images} max={3} onChange={(images) => set('images', images)} />
            <p className="mt-1 text-xs text-muted-foreground">
              Used when a shot shows this character with "reference pictures" — the video keeps its look.
            </p>
          </div>
          <div>
            <div className="mb-1.5 text-sm font-medium">Outfit (what they wear)</div>
            <ImageSlots images={d.outfit} max={1} onChange={(outfit) => set('outfit', outfit)} />
            <p className="mt-1 text-xs text-muted-foreground">
              A picture of the clothes: a dress, a uniform, a product photo. The character sheet is drawn wearing exactly
              these clothes (press <i>Redraw all</i> on the project's Characters tab after changing it).
            </p>
          </div>
          <Field
            label="Background (locked)"
            hint="Every still and shot with this character is set in this place. Add places on the Backgrounds page."
          >
            {(p) => (
              <NativeSelect {...p} value={d.backgroundId ?? ''} onChange={(e) => set('backgroundId', e.target.value || null)}>
                <option value="">— none —</option>
                {backgrounds.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Notes" className="sm:col-span-2">
            {(p) => <Textarea {...p} rows={2} value={d.notes} onChange={(e) => set('notes', e.target.value)} />}
          </Field>
        </div>
        <DialogFooter>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!d.name.trim()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

